import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog } from './dialog';
import { Button } from './button';
import { Select } from './select';
import { useClients } from '@/lib/clients';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Tab key on the client detail page to land on (e.g. "projects", "documents"). */
  tab: string;
  submitLabel?: string;
};

/**
 * Shared "where should this new thing live?" picker. Index pages without a
 * client-agnostic create flow open this, then route to the chosen client's
 * detail page on the relevant tab, where the in-tab create button takes over.
 */
export function PickClientDialog({ open, onClose, title, tab, submitLabel = 'Continue' }: Props) {
  const clients = useClients({ status: 'active' });
  const [clientId, setClientId] = useState<string>('');
  const navigate = useNavigate();

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!clientId) return;
    navigate(`/clients/${clientId}?tab=${tab}`);
    setClientId('');
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} title={title} size="sm">
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Client</span>
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
            <option value="">Pick a client…</option>
            {(clients.data?.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
          <span className="block text-xs text-[var(--color-foreground-muted)]">
            Items always belong to a client — pick one to continue.
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!clientId}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
